// The full-screen chat on a phone behaves like an iOS sheet: dragged down by
// its header it follows the finger, and on release it either closes or
// springs back (docs/022). The numbers follow Apple's "Designing Fluid
// Interfaces": decide from where the gesture is going, not where it stopped.

/** A press becomes a drag only after this many px, so taps on the header's buttons stay taps. */
export const DRAG_THRESHOLD = 10;

/** Only the last moments of a drag say how fast the finger was going when it let go. */
const VELOCITY_WINDOW_MS = 100;

export interface Sample {
  /** Screen y of the finger, px. */
  y: number;
  /** Event time, ms. */
  t: number;
}

/**
 * Where momentum would carry the sheet, like scroll deceleration (Apple's
 * projection; 0.998 is the normal scroll feel). 1000 px/s carries it ~500 px.
 */
export function project(velocity: number, decelerationRate = 0.998): number {
  return ((velocity / 1000) * decelerationRate) / (1 - decelerationRate);
}

/** px/s over the last 100 ms of samples, positive downwards; 0 when there is too little to tell. */
export function releaseVelocity(samples: Sample[]): number {
  const last = samples.at(-1);
  if (!last) return 0;
  const recent = samples.filter((s) => last.t - s.t <= VELOCITY_WINDOW_MS);
  const first = recent[0]!;
  const dt = last.t - first.t;
  return dt < 8 ? 0 : ((last.y - first.y) / dt) * 1000;
}

/**
 * Closes when the projected end is past half the sheet. A flick closes from
 * anywhere, a slow drag past the middle closes, a last move upwards keeps it.
 */
export function shouldDismiss(dy: number, velocity: number, height: number): boolean {
  if (velocity < 0) return false;
  return dy + project(velocity) > height / 2;
}

/**
 * How long the sheet takes to leave from where the finger let it go: it
 * starts at about the finger's speed (the drawer curve starts ~2.2x its mean
 * speed), within what still reads as a quick, deliberate exit.
 */
export function closeDuration(dy: number, velocity: number, height: number): number {
  const remaining = Math.max(0, height - dy);
  if (velocity <= 0 || remaining === 0) return 300;
  return Math.round(Math.min(320, Math.max(160, ((2.2 * remaining) / velocity) * 1000)));
}
